import React, { useState } from 'react';
import {
  Spin,
  Flex,
  Form as AForm,
  Input,
  Modal as AModal,
  Select,
  Typography,
  Checkbox,
} from 'antd';
import { Button } from 'shared/ui/Button/Button';
import { useCurrentUserDetails } from '../../../entities/user/model';
import {
  InformationCircleIcon,
  IdentificationIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { ROLES } from '../../../shared/hooks';

import {
  BanknotesIcon,
  ArrowsRightLeftIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { countries } from '../../../shared/ui/form/CountrySelect';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { sendTransactionLog } from '../../analytics/sendTransactionLog';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const { Text } = Typography;

export const usePaymentsOnboardingState = () => {
  const { hasBridgeIntegration, hasEasyPayIntegration } =
    useCurrentUserDetails();
  return {
    isOnboarded: hasBridgeIntegration() || hasEasyPayIntegration(),
  };
};

const TERMINAL_KYC_STATES = ['approved', 'rejected'];
const KYC_POLL_INTERVAL_MS = 1000 * 60;

const useKybcApplicationStatus = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { getCurrentOrganizationId, hasAnyOrganization } =
    useCurrentUserDetails();
  const query = useQuery({
    queryKey: ['kyc-application-status', getCurrentOrganizationId()],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/public/organisation/kybc-application/status`,
        {
          params: {
            organizationId: getCurrentOrganizationId(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return res.data?.result || null;
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
    // The customer finishes the flow on Bridge's side, in another tab, so keep
    // polling until the outcome is final and refresh the moment they come back.
    // Nothing to poll for without a Bridge customer behind the application.
    refetchInterval: ({ state }) => {
      const result = state.data;

      if (!result?.canPoll || TERMINAL_KYC_STATES.includes(result.state)) {
        return false;
      }

      return KYC_POLL_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
    enabled: hasAnyOrganization,
  });

  const result = query.data;

  return {
    hasApplication: Boolean(result?.hasApplication),
    state: result?.state,
    headline: result?.headline,
    detail: result?.detail,
    requiresUserAction: Boolean(result?.requiresUserAction),
    termsAccepted: result?.termsAccepted !== false,
    kycFlowLink: result?.kycFlowLink,
    termsLink: result?.termsLink,
    isPending: query.isPending,
  };
};

const useCreateKybcApplication = () => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const mutation = useMutation({
    mutationKey: ['create-kyc-application'],
    mutationFn: async (payload) => {
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/public/organisation/kybc-application`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await queryClient.invalidateQueries({
        queryKey: ['kyc-application-status'],
      });
    },
  });
  return {
    isCreating: mutation.isPending,
    createApplication: mutation.mutateAsync,
  };
};

export const PaymentsOnboardingModal = ({ show, onClose }) => {
  const [form] = AForm.useForm();
  const { data, currentOrganization, getCurrentOrganizationId } =
    useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();

  const { createApplication, isCreating } = useCreateKybcApplication();

  const _handleCreateApplication = () => {
    form
      .validateFields()
      .then(() => {
        const formData = form.getFieldsValue();
        const {
          customerName,
          customerType,
          email,
          termsConsent,
          servicesOfInterest,
        } = formData;

        createApplication({
          organizationId: getCurrentOrganizationId(),
          application: {
            customerName,
            servicesOfInterest,
            customerAddress: {
              streetAddress: '-',
              postalCode: '-',
              countryAlpha2: formData['customerAddress.countryAlpha2'],
              city: formData['customerAddress.city'],
            },
            customerType,
            email,
            termsConsent,
          },
        })
          .then(() => {
            onClose();
            toast.success('Success');
          })
          .catch(() => {
            sendTransactionLog(getAccessTokenSilently, {
              title: '[FAIL][KYC]',
              formData,
            });
            toast.error(
              'Was not able to create KYC application, please contact support: pavel@getfractal.xyz',
              {
                autoClose: 6000,
              }
            );
          });
      })
      .catch((res) => {
        console.log(res);
      });
  };

  return (
    <AModal
      title={'Tell us more about you'}
      open={show}
      loading={isCreating}
      centered
      footer={null}
      destroyOnClose
      onCancel={onClose}>
      <AForm
        name="basic"
        form={form}
        requiredMark={false}
        style={{
          paddingTop: 20,
          paddingBottom: 20,
        }}
        initialValues={{
          customerType: 'business',
          email: data.email,
          customerName: currentOrganization.name,
        }}
        layout="vertical"
        autoComplete="off">
        <AForm.Item
          rules={[
            {
              required: true,
              message: 'Select type',
            },
          ]}
          name="customerType"
          layout="vertical"
          label="Are you registering as an individual or a business?">
          <Select
            options={[
              {
                value: 'business',
                label: 'Business',
              },
              {
                value: 'individual',
                label: 'Individual',
              },
            ]}
          />
        </AForm.Item>

        <AForm.Item dependencies={['customerType']} noStyle>
          {() => {
            return (
              <Flex gap={'middle'}>
                <AForm.Item
                  className={'flex-1'}
                  rules={[
                    {
                      required: true,
                      message: 'Please enter name',
                    },
                  ]}
                  name="customerName"
                  layout="vertical"
                  label={`${
                    form.getFieldValue('customerType') === 'individual'
                      ? 'Customer'
                      : 'Business'
                  } Name`}>
                  <Input placeholder={'e.g. "John Doe"'} />
                </AForm.Item>
                <AForm.Item
                  className={'flex-1'}
                  rules={[
                    {
                      required: true,
                      message: 'Please enter email',
                      type: 'email',
                    },
                  ]}
                  name="email"
                  layout="vertical"
                  label="Contact Email">
                  <Input />
                </AForm.Item>
              </Flex>
            );
          }}
        </AForm.Item>

        <AForm.Item
          layout="vertical"
          rules={[
            {
              required: true,
              message: 'Select service of interest',
            },
          ]}
          label="Choose Services"
          name="servicesOfInterest">
          <Checkbox.Group>
            <Flex vertical={true} gap={'middle'}>
              <Checkbox value={'transfers'}>
                <Flex align={'center'} gap={'small'}>
                  Exchange{' '}
                  <ArrowsRightLeftIcon className={'w-4 text-indigo-300'} />
                </Flex>

                <span className={'text-slate-500'}>
                  Seamlessly buy and sell stablecoins. Transfers are restricted
                  to accounts under your name only.
                </span>
              </Checkbox>

              <Checkbox value={'3d_party_payments'}>
                <Flex align={'center'} gap={'small'}>
                  Payments <BanknotesIcon className={'w-4 text-indigo-300'} />
                </Flex>

                <span className={'text-slate-500'}>
                  Easily make crypto-to-fiat and fiat-to-crypto payments to
                  third parties, including employees, contractors, and vendors.
                </span>
              </Checkbox>
            </Flex>
          </Checkbox.Group>
        </AForm.Item>

        <AForm.Item dependencies={['customerType']} noStyle>
          {() => (
            <Flex gap={'middle'}>
              <AForm.Item
                className={'flex-1'}
                layout="vertical"
                label={`${
                  form.getFieldValue('customerType') === 'individual'
                    ? 'Residence'
                    : 'Business'
                } Country`}
                rules={[
                  {
                    required: true,
                    message: 'Select country',
                  },
                ]}
                name="customerAddress.countryAlpha2">
                <Select
                  showSearch={false}
                  placeholder={'Select country'}
                  options={countries.map(({ label, value, flag }) => ({
                    value,
                    label: (
                      <>
                        {flag} {label}
                      </>
                    ),
                  }))}
                />
              </AForm.Item>
              <AForm.Item
                className={'flex-1'}
                layout="vertical"
                label={`${
                  form.getFieldValue('customerType') === 'individual'
                    ? 'Residence'
                    : 'Business'
                } City`}
                rules={[
                  {
                    required: true,
                    message: 'Enter city',
                  },
                ]}
                name="customerAddress.city">
                <Input />
              </AForm.Item>
            </Flex>
          )}
        </AForm.Item>

        <AForm.Item
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error('To continue accept terms and conditions')
                    ),
            },
          ]}
          name="termsConsent"
          layout="vertical">
          <Checkbox>
            I agree for the
            <a
              className="ml-1 font-medium text-primary-600 hover:underline dark:text-indigo-500"
              href="/terms"
              target="_blank">
              Terms and Conditions
            </a>
          </Checkbox>
        </AForm.Item>
      </AForm>

      <div className="sm:grid sm:grid-cols-2 sm:gap-3">
        <Button color="gray" size={'sm'} onClick={onClose}>
          Close
        </Button>
        <Button size={'sm'} color="emerald" onClick={_handleCreateApplication}>
          <IdentificationIcon className="w-4 mr-2" />
          Start Verification
        </Button>
      </div>
    </AModal>
  );
};

export const PaymentsOnboardingWidget = () => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    hasApplication,
    state,
    headline,
    detail,
    requiresUserAction,
    termsAccepted,
    kycFlowLink,
    termsLink,
    isPending,
  } = useKybcApplicationStatus();
  const { isOnboarded } = usePaymentsOnboardingState();
  const { hasAnyOrganization, getCurrentOrgRole, data } =
    useCurrentUserDetails();
  const [showPaymentsOnboardingModal, setShowPaymentsOnboardingModal] =
    useState(false);
  const _handleClosePaymentsOnboarding = () => {
    setShowPaymentsOnboardingModal(false);
  };

  if (
    isOnboarded ||
    !hasAnyOrganization ||
    getCurrentOrgRole() !== ROLES.ADMIN
  ) {
    return null;
  }

  const renderInner = () => {
    if (isPending) {
      return (
        <Flex align={'center'} justify={'center'} className={'py-6'}>
          <Spin />
        </Flex>
      );
    }

    if (!hasApplication) {
      return (
        <Flex vertical align={'center'} className={'py-4 text-center'}>
          <Flex vertical align={'center'} gap={'small'} className={'mb-8'}>
            <Text className={'!text-slate-100 !text-lg font-semibold'}>
              Unlock payments and money movement
            </Text>
            <Text className={'!text-slate-400 max-w-xl'}>
              Verify your identity once to enable everything below.
            </Text>
          </Flex>

          <PaymentFeaturesDescription />

          <Button
            size={'sm'}
            color="emerald"
            className="mt-8 px-8"
            onClick={() => {
              sendTransactionLog(getAccessTokenSilently, {
                event: '👤 KYC FORM OPENED',
                email: data.email,
              });
              setShowPaymentsOnboardingModal(true);
            }}>
            <IdentificationIcon className="w-4 mr-2" />
            Verify identity
          </Button>
        </Flex>
      );
    }

    const isRejected = state === 'rejected';
    const isUnderReview = state === 'under_review';
    // We only get here when the organization has no integration yet, so an
    // approved application means the account is not switched on at our end.
    // Say so, rather than rendering nothing and leaving them with no payments
    // and no explanation.
    const isAwaitingSetup = state === 'approved';
    const isWaiting = isUnderReview || isAwaitingSetup;
    const StatusIcon = isRejected
      ? ExclamationTriangleIcon
      : isWaiting
        ? ClockIcon
        : InformationCircleIcon;
    const statusIconColor = isRejected
      ? 'text-red-400'
      : isWaiting
        ? 'text-sky-300'
        : 'text-amber-300';
    const detailText = isAwaitingSetup
      ? 'Your verification is approved and we are finishing your account setup. Payments will be enabled shortly, and nothing else is needed from you.'
      : detail;

    return (
      <>
        <Flex align={'flex-start'} vertical gap={'middle'}>
          <Flex vertical gap={'small'}>
            <Flex align={'center'} gap={'small'}>
              <StatusIcon className={`w-4 ${statusIconColor}`} />
              <Text className={'!text-slate-200 font-medium'}>{headline}</Text>
            </Flex>

            <Text className={'!text-slate-400'}>{detailText}</Text>

            {!termsAccepted && (
              <Text className={'!text-slate-400'}>
                The terms of service still need to be accepted.
              </Text>
            )}

          </Flex>

          {(requiresUserAction || !termsAccepted) && (
            <Flex gap={'small'} wrap>
              {requiresUserAction && kycFlowLink && (
                <Button
                  color="emerald"
                  size={'xs'}
                  href={kycFlowLink}
                  target="_blank">
                  <ArrowTopRightOnSquareIcon className="w-4 mr-2" />
                  Continue Verification
                </Button>
              )}

              {!termsAccepted && termsLink && (
                <Button
                  color="lightSlate"
                  size={'xs'}
                  href={termsLink}
                  target="_blank">
                  <ArrowTopRightOnSquareIcon className="w-4 mr-2" />
                  Accept Terms of Service
                </Button>
              )}
            </Flex>
          )}
        </Flex>
      </>
    );
  };

  return (
    <>
      <Flex vertical={true} className="p-4 rounded-lg dark:bg-slate-800 mb-6">
        {renderInner()}
      </Flex>

      <PaymentsOnboardingModal
        show={showPaymentsOnboardingModal}
        onClose={_handleClosePaymentsOnboarding}
      />
    </>
  );
};

const features = [
  {
    name: 'On/Off-Ramp',
    description: 'Move money between stablecoins, USD, EUR & AED.',
    icon: ArrowsRightLeftIcon,
  },
  {
    name: 'Named Accounts',
    description:
      'Get USD & EUR account details in your own name to receive payments directly.',
    icon: BuildingLibraryIcon,
  },
];

const PaymentFeaturesDescription = () => {
  return (
    <dl className="grid w-full max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2">
      {features.map((feature) => (
        <div
          key={feature.name}
          className="flex flex-col items-center rounded-xl border border-slate-700/60 bg-slate-900/40 px-6 py-7 text-center transition-colors hover:border-indigo-500/60">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/10 ring-1 ring-indigo-400/30">
            <feature.icon className="h-6 w-6 text-indigo-300" />
          </span>
          <dt className="text-base font-semibold leading-6 text-slate-100">
            {feature.name}
          </dt>
          <dd className="mt-2 text-sm leading-6 text-slate-400">
            {feature.description}
          </dd>
        </div>
      ))}
    </dl>
  );
};
